import { sign } from "crypto";
import { useAllSitePageData } from "./use-all-site-page-data";

type InternalItemType =
    | "DatoCmsActivity"
    | "DatoCmsAnimalExhibit"
    | "DatoCmsRide"
    | "DatoCmsVendor";
export type SiteLinkObject = {
    label: string | null;
    url: string | null;
};

export const useGenerateSiteLink = (
    linkData:
        | Queries.NavigationLinkFieldsFragment
        | Queries.ButtonLinkFieldsFragment
        | null
): SiteLinkObject | null => {
    // As a convenience, we allow null to be passed in for linkData since there are cases where Dato may return null.
    // In this case, we return null immediately.
    if (linkData === null) {
        return null;
    }

    const allSitePageData = useAllSitePageData();

    if (linkData.__typename === "DatoCmsExternalLink") {
        return {
            label: linkData.linkLabel,
            url: linkData.url,
        };
    } else if (linkData.__typename === "DatoCmsInternalPageLink") {
        const page = allSitePageData.find(
            (page) => page.pageContext?.id === linkData.link?.id
        );
        return page ? { label: linkData.linkLabel, url: page.path } : null;
    } else {
        const allMatchingPages = allSitePageData.filter(
            (page) => page.pageContext?.id === linkData.link?.id
        );
        const targetSignatureEventId = linkData.signatureEvent?.id;
        // If we have a target signature event, find the matching page for that signature event, otherwise find a global page
        const matchingPage = targetSignatureEventId
            ? allMatchingPages.find(
                  (page) =>
                      page.pageContext?.signatureEventId ===
                      targetSignatureEventId
              )
            : allMatchingPages.find(
                  (page) => !page.pageContext?.signatureEventId
              );
        return matchingPage
            ? { label: linkData.linkLabel, url: matchingPage.path }
            : null;
    }
};

/**
 * Gets the Gatsby Page path for a given Dato model id.
 * If a signature event id is provided, the path for
 * that signature event is returned, otherwise the global page is returned.
 *
 * @param linkId The Dato model id to find a global page for
 * @param signatureEventId The signature event id to find a page for
 *
 * @returns The path of the matching page, or null if none is found
 */
export const getLinkById = (
    linkId: string | null,
    signatureEventId?: string | null
): string | null => {
    if (!linkId) {
        return null;
    }

    const allSitePageData = useAllSitePageData();

    const allMatchingPages = allSitePageData.filter(
        (page) => page.pageContext?.id === linkId
    );

    // If we have a target signature event, find the matching page for that signature event, otherwise find a global page
    if (signatureEventId) {
        const matchingPage = allMatchingPages.find(
            (page) => page.pageContext?.signatureEventId === signatureEventId
        );

        return matchingPage ? matchingPage.path : null;
    } else {
        const matchingPage = allMatchingPages.find(
            (page) => !page.pageContext?.signatureEventId
        );

        return matchingPage ? matchingPage.path : null;
    }
};

export const getLinkForSignatureEventLandingPage = (signatureEventId: string | undefined): string | null => {
    if (!signatureEventId) {
        return null;
    }

    const allSitePageData = useAllSitePageData();

    const matchingPage = allSitePageData.find(
        (page) =>
            page.pageContext?.signatureEventId === signatureEventId &&
            page.pageContext?.template ===
            "DatoCmsSignatureEventLandingPageTemplate"
    );

    return matchingPage ? matchingPage.path : null;
}

export const getLinkForPerformanceSeries = (
    performanceSeriesId: string | undefined
): string | null => {
    if (!performanceSeriesId) {
        return null;
    }

    const allSitePageData = useAllSitePageData();

    const matchingPage = allSitePageData
        .filter(
            (page) =>
                page.pageContext?.template ===
                "DatoCmsPerformanceSeriesTemplate"
        )
        .find(
            (page) =>
                page.pageContext?.performanceSeriesId === performanceSeriesId
        );

    return matchingPage ? matchingPage.path : null;
};

export const getLinkForGlobalEventsCalendar = (): string | null => {

    const allSitePageData = useAllSitePageData();

    const matchingPage = allSitePageData.find(
        (page) =>
            page.component.endsWith("templates/global-events-calendar.tsx")
    );

    return matchingPage ? matchingPage.path : null;
}

export const buildInternalItemLink = (
    type: InternalItemType,
    itemId: string,
    signatureEventId: string | null,
    label: string = "Learn More"
): Queries.ButtonLinkFields_DatoCmsInternalItemLink_Fragment => {
    return {
        __typename: "DatoCmsInternalItemLink",
        linkLabel: label,
        link: {
            __typename: type,
            id: itemId,
        },
        signatureEvent: signatureEventId ? { id: signatureEventId } : null,
    };
};
